import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { memo, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import useAuth from "../hooks/useAuth";
import useSubscriptionMonitor from "../hooks/useSubscriptionMonitor";
import {
  discoveryBrandsParamsSelector,
  discoveryBrandsSelector,
} from "../store/discovery/selector";
import { userDetailsSelector } from "../store/account/selector";
import { MetaDataSagaActions } from "../store/metadata/sagas";
import { DashoardAnalyticsSagaActions } from "../store/dashboardAnalytics/sagas";
import { DiscoverySagaActions } from "../store/discovery/sagas";
import { MetaDataReducerAction } from "../store/metadata/slice";
import routeConstant from "../utils/routeConstant";

const GuestGuard = memo(() => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isAuthenticated, isPathNameFolder } = useAuth();

  // Extract next param from query
  const searchParams = new URLSearchParams(location.search);
  const next = searchParams.get("next");

  // User details from Redux or fallback localStorage
  const userDetails =
    useSelector(userDetailsSelector) ||
    JSON.parse(localStorage.getItem("User") as string);

  const discoveryBrands = useSelector(discoveryBrandsSelector);
  const discoveryBrandsParams = useSelector(discoveryBrandsParamsSelector);

  const isInvitePending = userDetails?.isInvitedBySeat === "PENDING";
  const isInviteAccepted = userDetails?.isInvitedBySeat === "ACCEPTED";
  const hasPlan = Boolean(userDetails?.planId);
  const isInvitedUserAccessible =
    userDetails?.parentPlanDetails?.parentDeatils?._id &&
    !userDetails?.isInvitedUserAccesible;

  const isParentFreePlan = userDetails?.parentPlanDetails?.planType === "Free";

  // ---- Helper: Fetch Initial Data ----
 const fetchInitialData = useCallback(() => {
  dispatch(MetaDataReducerAction.resetSwipefileState());
  dispatch(MetaDataSagaActions.fetchFavouriteBrands());
  dispatch(MetaDataSagaActions.fetchTagLists());
  dispatch(MetaDataSagaActions.fetchBoardList());
  dispatch(DiscoverySagaActions.fetchDiscoveryList());

  if (!discoveryBrands?.data?.length) {
    dispatch(
      DiscoverySagaActions.fetchDiscoveryTopBrandList(discoveryBrandsParams)
    );
  }

  dispatch(MetaDataSagaActions.teamDetails());
}, [dispatch, discoveryBrands?.data?.length, discoveryBrandsParams]);


  useEffect(() => {
    if (!userDetails?._id) return;
    dispatch(DashoardAnalyticsSagaActions.fetchDashboardAnalyticsData());
  }, [userDetails?._id]);

  useEffect(() => {
    if (!userDetails?._id) return;

    const now = new Date();
    const planEndDate = userDetails?.parentPlanDetails?.endDate
      ? new Date(userDetails.parentPlanDetails.endDate)
      : null;

    // Normalize both dates to YYYY-MM-DD (ignoring time)
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const planEnd = planEndDate
      ? new Date(
          planEndDate.getFullYear(),
          planEndDate.getMonth(),
          planEndDate.getDate()
        )
      : null;

    // Check if plan expired (date only)
    const isPlanExpired = planEnd && today > planEnd;

    if (isPlanExpired || isParentFreePlan || isInvitedUserAccessible) {
      navigate(routeConstant.subscriptionExpired, { replace: true });
      return;
    }

    if (hasPlan || isInviteAccepted) {
      fetchInitialData();
    } else {
      if (isInvitePending) {
        navigate(routeConstant.invitedMember, { replace: true });
      } else {
        navigate(routeConstant.subscription, { replace: true });
      }
    }
  }, [userDetails]);

  // Monitor subscription expiry
  useSubscriptionMonitor();

  // ---- Auth Guard ----
  if (isAuthenticated || isPathNameFolder) {
    return <Outlet />;
  }

  const redirectUrl = location.pathname
    ? `?next=${
        next || encodeURIComponent(window.location.origin + location.pathname)
      }`
    : "";

  return (
    <Navigate
      to={`/landing-page${redirectUrl}`}
      state={{ from: location }}
      replace
    />
  );
});

export default GuestGuard;
